import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { Gem, RotateCcw, Users } from 'lucide-react'
import audioService from '../../../services/audioService'
import ProfileLink from '../../../components/ProfileLink'
import TeamTag from '../../../components/TeamTag'
import useOpenTeam from '../../../hooks/useOpenTeam'
import { buildTeamColorMap } from '../utils/teamColors'

/**
 * Карточка одной ставки. Вынесена отдельно, потому что рендерится и в блоке
 * банды, и как самостоятельная строка (одиночки и члены банды без тиммейтов).
 */
const RoyaleBetRow = ({ bet, showTicketRange, hideTeamTag = false, refundLabel }) => {
  const refund = Number(bet.refund || 0)

  return (
    <motion.div
      initial="enter"
      animate="center"
      variants={{
        enter: { opacity: 0, rotateY: -90, scale: 0.5 },
        center: {
          opacity: 1,
          rotateY: 0,
          scale: 1,
          transition: {
            duration: 0.65,
            ease: [0.34, 1.56, 0.64, 1],
            opacity: { duration: 0.2 }
          }
        }
      }}
      style={{
        transformStyle: 'preserve-3d',
        perspective: '1000px'
      }}
      className={`rounded-lg p-3 border transition-colors ${
        bet.win_bet
          ? 'border-accent-primary/60 bg-accent-primary/10'
          : refund > 0
            ? 'border-info/40 bg-info/5'
            : 'border-white/10 bg-dark-primary/60'
      } ${
        bet.your_bet
          ? 'ring-1 ring-accent-secondary/70'
          : ''
      }`}
    >
      {/* Отступы задают только нижние блоки (`mt-*`), у шапки их нет: у ставки
          чистыми кристаллами ни бейджа возврата, ни ряда скинов нет, и `mb-*`
          на шапке превращался в пустую полосу под строкой */}
      <div className="flex items-center gap-3">
        {/* Имя и аватар ставки кликабельны: slug === null → игрок не кликабелен */}
        <ProfileLink slug={bet.slug} className="flex items-center gap-2 min-w-0 flex-1">
          <img
            src={bet.ava}
            alt={bet.name}
            className="w-8 h-8 rounded-full object-cover"
          />
          <div className="min-w-0 flex-1">
            {/* Банда — отдельной строкой над ником: строка ставки узкая, одной
                строкой «[Банда] Ник» обрезается. Внутри блока банды название
                уже в шапке группы — там бейдж не дублируем. Обёртка — flex:
                у инлайнового бейджа появляется строчный strut, из-за которого
                пара строк съезжает вниз относительно аватара */}
            {!hideTeamTag && bet.team?.name && (
              <div className="flex min-w-0 leading-tight">
                <TeamTag team={bet.team} className="mr-0 min-w-0 text-[11px] leading-tight" />
              </div>
            )}
            <p className="truncate text-sm font-semibold leading-tight text-white">
              {String(bet.name || '')?.length > 20
                ? `${String(bet.name).slice(0, 20)}…`
                : bet.name}
            </p>
            {showTicketRange && (bet.ticket_start || bet.ticket_finish) && (
              <p className="text-[11px] text-text-secondary">
                {Number(bet.ticket_start || 0).toLocaleString()} – {Number(bet.ticket_finish || 0).toLocaleString()}
              </p>
            )}
          </div>
        </ProfileLink>
        <span className="text-accent-primary text-md inline-flex items-center gap-1 shrink-0 ml-auto text-right">
          {Number(bet.total_crystal || 0).toLocaleString()}
          <Gem className="w-3.5 h-3.5 text-accent-primary" />
        </span>
      </div>

      {/* Возврат тиммейту победителя — это не проигрыш, отдельный нейтральный бейдж */}
      {refund > 0 && (
        <div className="mt-2 flex items-center gap-1 text-[11px] font-semibold text-info">
          <RotateCcw className="h-3 w-3" />
          <span>{refundLabel}</span>
          <span className="tabular-nums">{refund.toLocaleString()}</span>
          <Gem className="h-3 w-3" />
        </div>
      )}

      {/* Ставка только кристаллами — сумма уже в шапке, карточки не дублируем.
          Ряд показываем только когда есть скины (кристаллы идут первой карточкой). */}
      {bet.skins && bet.skins.length > 0 ? (
        <div className="flex gap-2 flex-wrap mt-4">
          {Number(bet.crystal || 0) > 0 && (
            <div className="w-16 h-16 rounded-lg bg-dark-primary/80 border border-accent-primary/40 flex flex-col items-center justify-center gap-2">
              <Gem className="w-4 h-4 text-accent-primary" />
              <span className="text-[10px] font-semibold text-white leading-none">
                {Number(bet.crystal || 0).toLocaleString()}
              </span>
            </div>
          )}
          {(bet.skins || []).map((skin) => (
            <div
              key={skin.id}
              className="w-16 h-16 rounded-lg bg-dark-primary/80 border"
              style={{ borderColor: `#${skin.color}` }}
            >
              <img
                src={skin.img}
                alt={skin.name}
                className="w-full h-full object-contain p-2"
                loading="lazy"
              />
            </div>
          ))}
        </div>
      ) : null}
    </motion.div>
  )
}

const RoyaleBetsFeed = ({ bets = [], teams = [], showTicketRange = false }) => {
  const { t } = useTranslation()
  const openTeam = useOpenTeam()
  const prevBetIdsRef = useRef(null)

  useEffect(() => {
    const currentIds = new Set((bets || []).map((bet) => String(bet.id)))

    if (!prevBetIdsRef.current) {
      prevBetIdsRef.current = currentIds
      return
    }

    const hasNewBet = Array.from(currentIds).some((id) => !prevBetIdsRef.current.has(id))
    if (hasNewBet) {
      audioService.play('royaleBetAccepted')
    }

    prevBetIdsRef.current = currentIds
  }, [bets])
  if (!bets || bets.length === 0) {
    return (
      <div></div>
    )
  }

  // Сортировка: новые ставки сверху (по ticket_start по убыванию)
  const sortedBets = [...bets].sort((a, b) => {
    const aTicket = Number(a.ticket_start || 0)
    const bTicket = Number(b.ticket_start || 0)
    return bTicket - aTicket
  })

  // Группировка по бандам с 2+ участниками: ставки тиммейтов склеиваются в один
  // блок. Группа встаёт на место своей самой новой ставки, поэтому порядок
  // «новое сверху» сохраняется. Одиночки и члены банды без тиммейтов в раунде
  // остаются обычными строками.
  const teamColors = buildTeamColorMap(teams)
  const teamsBySlug = new Map((teams || []).filter((team) => team?.slug).map((team) => [team.slug, team]))
  const groupIndexBySlug = new Map()
  const groups = []

  sortedBets.forEach((bet) => {
    const slug = bet.team?.slug
    if (!slug || !teamColors.has(slug)) {
      groups.push({ key: `solo:${bet.id}`, bets: [bet] })
      return
    }

    const existingIndex = groupIndexBySlug.get(slug)
    if (existingIndex !== undefined) {
      groups[existingIndex].bets.push(bet)
      return
    }

    groupIndexBySlug.set(slug, groups.length)
    groups.push({
      key: `team:${slug}`,
      // Снапшот банды у ставки не содержит доли и состава — их берём из teams[]
      team: teamsBySlug.get(slug) || bet.team,
      color: teamColors.get(slug),
      bets: [bet]
    })
  })

  const refundLabel = t('royale.betsFeed.refunded')

  return (
    <div className="">
      <h2 className="text-white font-semibold mb-3">{t('royale.betsFeed.title')}</h2>
      <div className="space-y-3">
        {groups.map((group) => {
          if (!group.team) {
            return (
              <RoyaleBetRow
                key={group.key}
                bet={group.bets[0]}
                showTicketRange={showTicketRange}
                refundLabel={refundLabel}
              />
            )
          }

          const percent = Number(group.team.percent || 0)
          const refund = Number(group.team.refund || 0)

          return (
            <div
              key={group.key}
              className="relative overflow-hidden rounded-xl border border-white/10 bg-surface/50 p-2"
              style={{ borderLeft: `3px solid ${group.color}` }}
            >
              <div
                className="pointer-events-none absolute inset-x-0 top-0 h-14"
                style={{ background: `linear-gradient(180deg, ${group.color}1F, transparent)` }}
              />

              <div className="relative flex items-center gap-2 px-1 py-1.5">
                <img
                  src={group.team.ava || `https://ui-avatars.com/api/?name=${encodeURIComponent(group.team.name || 'Team')}&background=A78BFA&color=0A0A0F`}
                  alt={group.team.name}
                  className="h-7 w-7 shrink-0 rounded-lg border object-cover"
                  style={{ borderColor: group.color }}
                  loading="lazy"
                />
                <p
                  onClick={group.team.slug ? (event) => openTeam(event, group.team.slug) : undefined}
                  className={`min-w-0 flex-1 truncate text-xs font-semibold text-white ${
                    group.team.slug ? 'cursor-pointer transition-transform active:scale-[0.98]' : ''
                  }`}
                  title={group.team.name}
                >
                  {group.team.name}
                </p>
                <span className="inline-flex shrink-0 items-center gap-1 text-[11px] tabular-nums text-text-secondary">
                  <Users className="h-3 w-3" />
                  {Number(group.team.count_users || group.bets.length)}
                </span>
                {percent > 0 && (
                  <span className="shrink-0 text-xs font-bold tabular-nums text-white">
                    {percent.toFixed(2)}%
                  </span>
                )}
                <span
                  className="hidden shrink-0 text-info"
                  title={refund > 0 ? t('royale.teams.refundDone') : t('royale.teams.refundHint')}
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                </span>
              </div>

              <div className="relative mt-1 space-y-2">
                {group.bets.map((bet) => (
                  <RoyaleBetRow
                    key={bet.id}
                    bet={bet}
                    showTicketRange={showTicketRange}
                    hideTeamTag
                    refundLabel={refundLabel}
                  />
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default RoyaleBetsFeed
